// "Use my location": which district a position is in, found in THIS browser from the district
// map the worker publishes (worker/sr/districts.py, whose `locate` this follows line for line;
// web/scripts/check-locate.mjs holds the two to the same answers). No imports, so the check can
// run it in Node as it is.
//
// THE POSITION NEVER LEAVES THE DEVICE: every visitor downloads the same whole-country map, and
// the position is held only in the variables below while the district is found.
//
// NEAR A LINE IS NOT GUESSED. The map's lines are within `tolerance_m` of the Census Bureau's own,
// and the browser says how far its position may be off (`accuracy`). Every district whose line
// comes within both of those is returned in `near`; when that is more than the one the position
// falls in, the page asks rather than picks.

export interface DistrictMap {
  format: number;
  congress: number;
  source: string;
  tolerance_m: number;
  scale: number;
  districts: {
    state: string;
    seat: string | null;
    kind: 'seat' | 'delegate' | 'none';
    bbox: [number, number, number, number];
    rings: number[][];
  }[];
  // States whose lines for this Congress the Census does not have, left off the map rather than drawn
  // wrong: the 117th's North Carolina (worker/sr/sources/tiger.py LEFT_OUT). Absent on other maps.
  unavailable?: string[];
}

export interface Located {
  found: number | null;   // the district the position falls in on this map
  near: number[];         // every district whose line is within tolerance + accuracy
}

const M_PER_DEG_LAT = 111320;

// Rings decoded to absolute coordinates once per map, and only for districts ever looked at.
const decodedCache = new WeakMap<DistrictMap, Map<number, number[][]>>();

function decode(flat: number[]): number[] {
  const out = new Array<number>(flat.length);
  let x = flat[0], y = flat[1];
  out[0] = x; out[1] = y;
  for (let i = 2; i < flat.length; i += 2) {
    x += flat[i]; y += flat[i + 1];
    out[i] = x; out[i + 1] = y;
  }
  return out;
}

function inside(rings: number[][], x: number, y: number): boolean {
  let hit = false;
  for (const r of rings) {
    for (let i = 0; i + 3 < r.length; i += 2) {
      const x0 = r[i], y0 = r[i + 1], x1 = r[i + 2], y1 = r[i + 3];
      if ((y0 > y) !== (y1 > y) && x < x0 + ((y - y0) * (x1 - x0)) / (y1 - y0)) hit = !hit;
    }
  }
  return hit;
}

function distanceM(rings: number[][], x: number, y: number, kx: number, ky: number): number {
  let best = Infinity;
  for (const r of rings) {
    for (let i = 0; i + 3 < r.length; i += 2) {
      const ax = (r[i] - x) * kx, ay = (r[i + 1] - y) * ky, bx = (r[i + 2] - x) * kx, by = (r[i + 3] - y) * ky;
      const dx = bx - ax, dy = by - ay, seg = dx * dx + dy * dy;
      const t = seg === 0 ? 0 : Math.max(0, Math.min(1, -(ax * dx + ay * dy) / seg));
      best = Math.min(best, Math.hypot(ax + t * dx, ay + t * dy));
    }
  }
  return best;
}

export function locate(map: DistrictMap, lon: number, lat: number, accuracyM: number): Located {
  const scale = map.scale;
  const x = Math.round(lon * scale), y = Math.round(lat * scale);
  const margin = map.tolerance_m + Math.max(0, accuracyM);
  const ky = M_PER_DEG_LAT / scale;
  const kx = ky * Math.cos((lat * Math.PI) / 180);
  const padX = margin / kx, padY = margin / ky;
  let decoded = decodedCache.get(map);
  if (!decoded) { decoded = new Map(); decodedCache.set(map, decoded); }
  let found: number | null = null;
  const near: number[] = [];
  map.districts.forEach((d, i) => {
    const [x0, y0, x1, y1] = d.bbox;
    if (!(x0 - padX <= x && x <= x1 + padX && y0 - padY <= y && y <= y1 + padY)) return;
    let rings = decoded!.get(i);
    if (!rings) { rings = d.rings.map(decode); decoded!.set(i, rings); }
    if (found === null && inside(rings, x, y)) found = i;
    if (distanceM(rings, x, y, kx, ky) <= margin) near.push(i);
  });
  return { found, near };
}
